import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface PaymentData {
  amount: number;
  creditsAmount: number;
  workspaceId: string;
}

interface PaymentIntentResponse {
  clientSecret: string;
  paymentIntentId: string;
}

interface ProcessPaymentData {
  paymentIntentId: string;
  creditsAmount: number;
  workspaceId: string;
}

export function useStripePayment() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const createPaymentIntent = useMutation({
    mutationFn: async ({ amount }: { amount: number }): Promise<PaymentIntentResponse> => {
      const { data, error } = await supabase.functions.invoke('create-payment-intent', {
        body: { amount }
      });

      if (error) throw error;
      return data;
    },
    onError: (error) => {
      console.error('Payment intent creation failed:', error);
      toast({
        title: "Payment Setup Failed",
        description: "Unable to initialize payment. Please try again.",
        variant: "destructive",
      });
    }
  });

  const processPayment = useMutation({
    mutationFn: async ({ paymentIntentId, creditsAmount, workspaceId }: ProcessPaymentData) => {
      const { data, error } = await supabase.functions.invoke('process-credit-purchase', {
        body: { 
          paymentIntentId, 
          creditsAmount, 
          workspaceId 
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      toast({
        title: "Credits Added Successfully!",
        description: `${data.creditsAdded} credits have been added to your account. New balance: ${data.newBalance}`,
      });
      
      // Invalidate credit-related queries to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['workspace-credits'] });
      queryClient.invalidateQueries({ queryKey: ['credit-transactions'] });
    },
    onError: (error) => {
      console.error('Payment processing failed:', error);
      toast({
        title: "Payment Processing Failed",
        description: "Your payment could not be processed. Please contact support if the charge was made.",
        variant: "destructive",
      });
    }
  });

  return {
    createPaymentIntent,
    processPayment,
    isCreatingIntent: createPaymentIntent.isPending,
    isProcessingPayment: processPayment.isPending,
  };
}